// ============================================================================
// FIX-CAPTURE-TRANCHE-2B-W3 — the capture card as a VOICE prompt.
//
// Rulings encoded here, not left to the caller:
//   * R-CAP-2 — every question is a voice note answered by a green tick or a red X.
//               NO CLIP, NO CARD: if the clip path is missing, the signed URL cannot be
//               resolved, or playback errors, this component renders nothing and tells the
//               provider to move on. It NEVER falls back to text.
//   * R-CAP-5 — the card renders ZERO text. No question text, no caption, no chips, no
//               labels, no counts. Voice + tick + X + replay only. (The buttons carry
//               `aria-label`s for screen readers: those are not rendered text — they never
//               appear in the subtree's textContent and are invisible to a sighted farmer.)
//   * R-CAP-3 — S058/S052 are answered as two-level atoms; the client sends the mapped
//               count and the server validates it. The registry spec amendment is W4.
//   * R-CAP-1 — no back-dating: there is no date question to render.
//
// Audio: ONE shared <audio> element for the whole app — the existing genAudioController
// singleton. stopGenAudio() runs before every play so a screen-entry note can never talk
// over a question, and the same monotonic token aborts a stale resolve.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, RotateCcw, X } from 'lucide-react';
import { LargeIconButton } from '@/features/capture/components/large-icon-button';
import { haptics } from '@/shared/lib/interactions/haptics';
import { playAudioUrl, stopGenAudio } from '@/features/agriagent/lib/genAudioController';
import { getVoicePromptUrl } from '@/features/agriagent/lib/voicePrompt';
import { useAuth } from '@/features/auth/components/AuthContext';
import { recordPromptEvent, type CaptureAnswer, type CaptureCard, type CaptureLang } from './captureApi';

/** The manifest question_key each card/step is asking (W2 layout). Telemetry only. */
export function questionKeyForStep(card: CaptureCard, step: 1 | 2): string {
  if (card.atom === 'NA-11') return step === 2 ? 'coping-sold-assets' : 'coping-affected';
  // FIX-CAPTURE-T3-W6: the three voice-only tick/X cards (register keys verbatim)
  if (card.atom === 'S097') return 'land-own-or-rent';
  if (card.atom === 'S098') return 'insured-assets';
  if (card.atom === 'S002') return 'settlement-confirm';
  return card.atom === 'S058' ? 'labour-over-three' : 'income-second-source';
}

/** How long the tick/X overlay shows before the answer is sent — VoiceConfirmation.tsx:31-43. */
export const VOICE_FEEDBACK_MS = 500;

export interface VoicePromptProps {
  card: CaptureCard;
  busy?: boolean;
  /** Language tag recorded with each prompt event (telemetry only; the clip decides what is heard). */
  lang?: CaptureLang;
  /** Send exactly one answer for this card, then close it. */
  onAnswer: (answer: CaptureAnswer) => void;
  /** No clip, no card: drop this card WITHOUT recording anything and move on. */
  onUnavailable: () => void;
}

/** Which manifest clip this step plays. Step 2 exists only for the NA-11 follow-up. */
export function clipPathForStep(card: CaptureCard, step: 1 | 2): string | null | undefined {
  if (step === 2) return card.atom === 'NA-11' ? card.follow_up_clip_path : null;
  return card.clip_path;
}

/**
 * The answer a tick (true) or an X (false) means, per R-CAP-2/R-CAP-3.
 * `null` means "not an answer yet" — the only case is a tick on NA-11 step 1, which opens
 * the follow-up question instead of submitting.
 *
 *   NA-11  step 1  X    → affected=false          (server records coping 'not-affected')
 *          step 1  tick → null                    (play follow_up_clip_path as step 2)
 *          step 2  tick → 'asset-sold'            (erosive)
 *          step 2  X    → 'none-recorded'         (neutral)
 *   S058          tick → count 4   ·  X → count 2
 *   S052          tick → count 2   ·  X → count 1
 *   T3-W6 (R-T3-9/10/11) S097 / S098 / S002   tick → {yes:true}  ·  X → {yes:false}
 *          S097 "Do you own the land you farm?"                       tick = owner (S097), X = tenant (S079)
 *          S098 "Are your animals, crops or equipment insured?"      X is a real answer (NOT insured)
 *          S002 "Your buyer recorded your harvest. Was the amount right?"  tick corroborates, X raises a review flag
 */
export function voiceAnswerFor(card: CaptureCard, step: 1 | 2, tick: boolean): CaptureAnswer | null {
  if (card.atom === 'NA-11') {
    if (step === 1) return tick ? null : { affected: false };
    return { affected: true, action_type: tick ? 'asset-sold' : 'none-recorded' };
  }
  if (card.atom === 'S097' || card.atom === 'S098' || card.atom === 'S002') return { yes: tick };
  if (card.atom === 'S058') return { count: tick ? 4 : 2 };
  return { count: tick ? 2 : 1 };
}

export function VoicePrompt({ card, busy = false, lang = 'en', onAnswer, onUnavailable }: VoicePromptProps) {
  const { user } = useAuth();
  const token = user?.sessionToken;
  const [step, setStep] = useState<1 | 2>(1);
  const [ready, setReady] = useState(false);
  const [feedback, setFeedback] = useState<'tick' | 'x' | null>(null);
  const answeredRef = useRef(false);          // double-tap guard: ONE answer per card
  const unavailableRef = useRef(false);       // one "give up" per card, never twice
  const urlRef = useRef<string | null>(null); // resolved URL for the replay button
  const renderedRef = useRef<Set<number>>(new Set());  // steps already reported as rendered
  const liveRef = useRef({ card, step: 1 as 1 | 2, lang, token });

  // Keep the latest values reachable from the unmount cleanup without re-running it.
  liveRef.current = { card, step, lang, token };

  /** Fire-and-forget prompt telemetry. Never awaited, never throws (captureApi swallows). */
  const emit = useCallback((event: Parameters<typeof recordPromptEvent>[3], atStep: 1 | 2) => {
    const c = liveRef.current.card;
    recordPromptEvent(
      liveRef.current.token, c.atom, c.subject_ref, event,
      questionKeyForStep(c, atStep), clipPathForStep(c, atStep) ?? null, liveRef.current.lang,
    );
  }, []);

  const giveUp = useCallback(() => {
    if (unavailableRef.current) return;
    unavailableRef.current = true;
    emit('failed', liveRef.current.step);
    setReady(false);
    onUnavailable();
  }, [emit, onUnavailable]);

  // Resolve + auto-play the clip for the current step. A missing path, an unresolvable
  // signed URL or a playback error all mean: no card.
  useEffect(() => {
    let cancelled = false;
    const path = clipPathForStep(card, step);
    setReady(false);
    if (!path) { giveUp(); return; }

    // Stop any screen-entry note FIRST so it cannot talk over the question.
    stopGenAudio();

    void (async () => {
      const url = await getVoicePromptUrl(path);
      if (cancelled) return;
      if (!url) { giveUp(); return; }
      urlRef.current = url;
      const played = await playAudioUrl(url, () => { if (!cancelled) giveUp(); });
      if (cancelled) return;
      if (played) {
        if (!renderedRef.current.has(step)) { renderedRef.current.add(step); emit('rendered', step); }
        setReady(true);
      } else giveUp();
    })();

    return () => { cancelled = true; };
  }, [card, step, giveUp, emit]);

  // Never let a question keep playing after the card closes. On the way out, a card the farmer
  // saw but never answered (and that did not fail) is a DISMISSAL — the only place we can see it,
  // since the dialog is closed by the shell.
  useEffect(() => () => {
    stopGenAudio();
    if (!answeredRef.current && !unavailableRef.current && renderedRef.current.size > 0) {
      emit('dismissed', liveRef.current.step);
    }
  }, [emit]);

  const replay = useCallback(() => {
    const url = urlRef.current;
    if (!url) return;
    emit('replayed', liveRef.current.step);
    stopGenAudio();
    void playAudioUrl(url, giveUp);
  }, [emit, giveUp]);

  const answer = useCallback((tick: boolean) => {
    if (answeredRef.current || busy) return;
    const mapped = voiceAnswerFor(card, step, tick);
    emit(tick ? 'answered_tick' : 'answered_x', step);
    tick ? haptics.success() : haptics.select();   // X is an answer, not a mistake: medium pulse
    setFeedback(tick ? 'tick' : 'x');
    if (mapped === null) {
      // NA-11 step 1 tick → ask the follow-up in the same card, after the same 500 ms beat.
      window.setTimeout(() => { setFeedback(null); setStep(2); }, VOICE_FEEDBACK_MS);
      return;
    }
    answeredRef.current = true;
    window.setTimeout(() => { stopGenAudio(); onAnswer(mapped); }, VOICE_FEEDBACK_MS);
  }, [busy, card, emit, onAnswer, step]);

  // No clip → nothing at all. The provider has already been told to move on.
  if (!ready && !feedback) return null;

  return (
    <div
      data-testid="voice-prompt"
      data-atom={card.atom}
      data-step={step}
      className="relative flex items-center justify-center gap-4 py-6"
    >
      <LargeIconButton
        icon={RotateCcw}
        label="Replay"
        size="xl"
        variant="ghost"
        silent
        disabled={busy || feedback !== null}
        onClick={replay}
        data-testid="voice-replay"
      />
      <LargeIconButton
        icon={X}
        label="No"
        size="xl"
        silent
        disabled={busy || feedback !== null}
        onClick={() => answer(false)}
        data-testid="voice-x"
        className="bg-error text-white shadow-lg shadow-error/30 hover:bg-error/90"
      />
      <LargeIconButton
        icon={Check}
        label="Yes"
        size="xl"
        silent
        disabled={busy || feedback !== null}
        onClick={() => answer(true)}
        data-testid="voice-tick"
        className="bg-success text-white shadow-lg shadow-success/30 hover:bg-success/90"
      />

      {/* 500 ms icon-only overlay — the VoiceConfirmation.tsx:150-167 feedback language,
          with its text stripped out (R-CAP-5). */}
      {feedback && (
        <div
          data-testid="voice-feedback"
          className={`absolute inset-0 flex items-center justify-center rounded-lg bg-background/80 animate-fade-in ${
            feedback === 'tick' ? 'text-success' : 'text-error'
          }`}
        >
          {feedback === 'tick'
            ? <Check className="w-16 h-16" strokeWidth={2.5} />
            : <X className="w-16 h-16" strokeWidth={2.5} />}
        </div>
      )}
    </div>
  );
}
